import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Play, Volume2, VolumeX, ArrowRight, Radio } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const B = process.env.REACT_APP_BACKEND_URL;

export function useBriefingMeta(cut = "full") {
  const [meta, setMeta] = useState(undefined);
  useEffect(() => {
    fetch(`${B}/api/marketing/kit/public/briefing/meta?cut=${cut}`).then((r) => (r.ok ? r.json() : null)).then(setMeta).catch(() => setMeta(null));
  }, [cut]);
  return meta;
}

export function LiveBriefingPlayer({ cut = "full", vertical = false, autoPlay = false, className = "", testId = "live-briefing-player" }) {
  const vid = useRef(null);
  const [muted, setMuted] = useState(autoPlay);
  const [playing, setPlaying] = useState(autoPlay);
  const fmt = vertical ? "9x16" : "16x9";
  const src = `${B}/api/marketing/kit/public/briefing?format=${fmt}&cut=${cut}&web=1`;
  const poster = `${B}/api/marketing/kit/public/briefing?format=${fmt}&cut=${cut}&poster=1`;
  return (
    <div className={`relative overflow-hidden rounded-2xl border border-white/15 bg-black shadow-2xl shadow-black/60 ${vertical ? "aspect-[9/16]" : "aspect-video"} ${className}`} data-testid={testId}>
      <video ref={vid} src={src} poster={poster} muted={muted} autoPlay={autoPlay} loop={autoPlay} playsInline preload="metadata" controls={playing && !autoPlay}
        onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} className="h-full w-full object-contain" />
      <div className="absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-full bg-[#FF3B30] px-2.5 py-1 text-[11px] font-black tracking-widest text-white" data-testid="live-briefing-badge">
        <span className="h-2 w-2 rounded-full bg-white animate-pulse" /> LIVE
      </div>
      {!playing && (
        <button type="button" data-testid="live-briefing-play" onClick={() => { vid.current.muted = false; setMuted(false); vid.current.play(); }}
          className="absolute inset-0 grid place-items-center bg-black/30 hover:bg-black/20 transition-colors">
          <span className="inline-flex items-center gap-2 rounded-full bg-white text-black px-5 h-12 font-bold text-sm shadow-xl"><Play className="h-4 w-4 fill-black" /> Watch the live briefing</span>
        </button>
      )}
      {autoPlay && (
        <button type="button" onClick={() => { const m = !muted; setMuted(m); vid.current.muted = m; }} data-testid="live-briefing-sound"
          className="absolute bottom-3 right-3 h-9 w-9 rounded-full bg-black/60 backdrop-blur grid place-items-center text-white border border-white/20">
          {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
        </button>
      )}
    </div>
  );
}

export function LiveBriefingCard({ compact = false }) {
  const meta = useBriefingMeta("90s");
  const { user } = useAuth();
  if (!meta) return null;
  return (
    <section className="rounded-2xl border border-[#FF3B30]/40 bg-[#120B0B] p-4 md:p-5" data-testid="live-briefing-card">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-[#FF3B30] font-bold"><Radio className="h-3.5 w-3.5 animate-pulse" /> VISMARKS global briefing</div>
      <h2 className="mt-2 text-base md:text-lg font-black tracking-tight">Transform your store into a global theatre — watch the briefing</h2>
      <LiveBriefingPlayer cut="90s" vertical={compact} className="mt-3" testId="live-briefing-card-player" />
      <div className="mt-3 flex flex-wrap items-center gap-3 text-sm">
        <Link to="/briefing" data-testid="live-briefing-full-link" className="text-[#007AFF] hover:underline inline-flex items-center gap-1">Full talk &amp; transcript <ArrowRight className="h-3.5 w-3.5" /></Link>
        {!user && <Link to="/register?role=seller" data-testid="live-briefing-signup" className="ml-auto inline-flex h-9 px-4 items-center rounded-full bg-[#FF3B30] text-white font-bold text-xs">Sign up free — 5 listings</Link>}
      </div>
    </section>
  );
}
